import React, { FC, ReactElement, ReactNode, useCallback } from 'react';

import { useFormContext } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import {
  ERewardCalendarMissedReset,
  ERewardCalendarPopUpTrigger,
  ERewardCalendarRewardCollection
} from 'constants/enums';

import { RadioButtonOptionsProps } from 'components/RadioButton/RadioButton';
import RadioButtonGroup from 'components/RadioButton/RadioButtonGroup';
import RichText from 'components/RichText/RichText';
import { cn } from 'components/lib/utils';
import { UISwitch } from 'components/ui';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from 'components/ui/tooltip';

import {
  MISSED_RESET_OPTIONS,
  POPUP_TRIGGER_OPTIONS,
  REWARD_COLLECTION_OPTIONS,
  RewardCalendarFormFields
} from '../../RewardCalendarForm.consts';

interface IBehaviorSectionProps {
  title: string;
  description?: string;
  tooltip?: string;
  rightHeader?: ReactNode;
  children?: ReactNode;
}

const BehaviorSection: FC<IBehaviorSectionProps> = ({
  title,
  description,
  tooltip,
  rightHeader,
  children
}): ReactElement => (
  <div className="flex flex-col gap-5 p-[17px] rounded-lg border border-base-sidebarBorder">
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <h4 className="text-p-m font-semibold text-base-foreground">{title}</h4>
        {tooltip && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-pointer text-base-mutedForeground">
                  <FontAwesomeIcon icon="fa-regular fa-circle-info" />
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                align="center"
                className="rounded-md border border-base-popover bg-base-popover shadow-md py-[6px] px-[12px]"
              >
                <span className="text-p-s text-base-popoverForeground">
                  {tooltip}
                </span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {rightHeader}
    </div>
    {description && (
      <p className="text-p-s text-base-mutedForeground">{description}</p>
    )}
    {children}
  </div>
);

interface IBehaviorRadioGroupProps {
  value: string;
  onValueChange: (value: string) => void;
  options: RadioButtonOptionsProps[];
}

const BehaviorRadioGroup: FC<IBehaviorRadioGroupProps> = ({
  value,
  onValueChange,
  options
}): ReactElement => (
  <RadioButtonGroup
    isColumn
    className="!gap-3 [&_label]:text-p-xs [&_p]:text-p-xs"
    itemClassName={[
      '!items-center',
      '[&>div]:flex [&>div]:flex-wrap [&>div]:items-baseline [&>div]:gap-x-1.5',
      '[&>div>label]:!mb-0',
      '[&>button]:!mt-0 [&>button]:!h-4 [&>button]:!w-4 [&>button]:!border [&>button]:shadow-sm',
      '[&>button>span>svg]:!h-2 [&>button>span>svg]:!w-2'
    ].join(' ')}
    value={value}
    onValueChange={onValueChange}
    options={options}
  />
);

const OfferBehaviorTab: FC = (): ReactElement => {
  const { watch, setValue } = useFormContext<RewardCalendarFormFields>();

  const startOver = watch('startOver');
  const missedRewardReset = watch('missedRewardReset');
  const popUpTrigger = watch('popUpTrigger');
  const rewardCollection = watch('rewardCollection');

  const handleStartOverChange = useCallback(
    (checked: boolean): void => {
      if (checked === startOver) return;
      setValue('startOver', checked, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [setValue, startOver]
  );

  const handleMissedRewardResetChange = useCallback(
    (value: string): void => {
      if (value === missedRewardReset) return;
      setValue('missedRewardReset', value as ERewardCalendarMissedReset, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [setValue, missedRewardReset]
  );

  const handlePopUpTriggerChange = useCallback(
    (value: string): void => {
      if (value === popUpTrigger) return;
      setValue('popUpTrigger', value as ERewardCalendarPopUpTrigger, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [setValue, popUpTrigger]
  );

  const handleRewardCollectionChange = useCallback(
    (value: string): void => {
      if (value === rewardCollection) return;
      setValue('rewardCollection', value as ERewardCalendarRewardCollection, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [setValue, rewardCollection]
  );

  return (
    <div className="flex flex-col">
      <RichText
        title="Calendar Behavior"
        description="Define restart logic, trigger conditions, and claiming mode."
        className="mb-5"
      />
      <div className="flex flex-col gap-5">
        <BehaviorSection
          title="Restart After Completion"
          tooltip="Choose what happens after all weeks are completed. When enabled, the calendar restarts from Week 1. When disabled, the calendar ends."
          rightHeader={
            <UISwitch
              checked={startOver}
              id="reward-calendar-start-over-switch"
              onCheckedChange={handleStartOverChange}
              className={cn(
                'shadow-none bg-base-input data-[state=unchecked]:bg-input',
                '[&>span]:bg-base-background',
                'h-6 w-11',
                'data-[state=checked]:!bg-indigo-600 [&>span]:!h-5 [&>span]:!w-5 data-[state=checked]:[&>span]:!translate-x-5'
              )}
            />
          }
        />

        <BehaviorSection
          title="Missed Reward Reset"
          tooltip="Choose what happens when a reward is not collected for a day."
        >
          <BehaviorRadioGroup
            value={missedRewardReset}
            onValueChange={handleMissedRewardResetChange}
            options={MISSED_RESET_OPTIONS}
          />
        </BehaviorSection>

        <BehaviorSection
          title="Pop-Up Trigger"
          tooltip="Choose when the calendar popup appears."
        >
          <BehaviorRadioGroup
            value={popUpTrigger}
            onValueChange={handlePopUpTriggerChange}
            options={POPUP_TRIGGER_OPTIONS}
          />
        </BehaviorSection>

        <BehaviorSection
          title="Reward Collection"
          tooltip="Choose how players collect their daily rewards."
        >
          <BehaviorRadioGroup
            value={rewardCollection}
            onValueChange={handleRewardCollectionChange}
            options={REWARD_COLLECTION_OPTIONS}
          />
        </BehaviorSection>
      </div>
    </div>
  );
};

export default OfferBehaviorTab;
